import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowDown, Mail, Linkedin, Facebook, X, Calendar, Download } from 'lucide-react';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  // Typing animation titles
  const titles = useMemo(() => [
    "Web Developer",
    "Full-Stack Product Developer (AI-Enabled)",
    "Digital Agency & Learning Institute Co-founder"
  ], []);
  
  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);
  const [wipeProgress, setWipeProgress] = useState(0);
  const [isWipingOut, setIsWipingOut] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Wipe animation effect
  useEffect(() => {
    const currentTitle = titles[currentTitleIndex];
    const wipeInSpeed = 30; // ms per character for wipe in
    const wipeOutSpeed = 20; // ms per character for wipe out
    const pauseTime = 2000; // pause when fully visible
    
    let timeout;
    
    if (!isWipingOut) {
      // Wipe In
      if (wipeProgress < currentTitle.length) {
        timeout = setTimeout(() => {
          setWipeProgress(prev => prev + 1);
        }, wipeInSpeed);
      } else {
        // Fully visible - pause then start wipe out
        timeout = setTimeout(() => {
          setIsWipingOut(true);
        }, pauseTime);
      }
    } else {
      // Wipe Out
      if (wipeProgress > 0) {
        timeout = setTimeout(() => {
          setWipeProgress(prev => prev - 1);
        }, wipeOutSpeed);
      } else {
        // Fully hidden - move to next title
        setIsWipingOut(false);
        setCurrentTitleIndex((prev) => (prev + 1) % titles.length);
      }
    }
    
    return () => clearTimeout(timeout);
  }, [wipeProgress, isWipingOut, currentTitleIndex, titles]);

  // Cutout professional image (transparent PNG with person and foreground)
  const heroImage = "/hero.png";

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const currentTitle = titles[currentTitleIndex];
  const visibleText = currentTitle.slice(0, wipeProgress);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* LEFT SIDE - Texture image with fade (DARK THEME ONLY) */}
      <div className="absolute left-0 top-0 w-[65%] h-full pointer-events-none dark-only">
        {/* Texture as img element */}
        <img 
          src="https://customer-assets.emergentagent.com/job_techportfolio-46/artifacts/5jqa2gtu_banner-shape-two.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-50"
          style={{ objectPosition: 'left center' }}
        />
        {/* Combined fade overlay - right + bottom */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(to right, transparent 0%, transparent 20%, rgba(10,10,15,0.7) 50%, rgb(10,10,15) 100%),
              linear-gradient(to bottom, transparent 0%, transparent 40%, rgba(10,10,15,0.4) 60%, rgba(10,10,15,0.8) 80%, rgb(10,10,15) 95%)
            `
          }}
        />
        {/* Dark red/maroon color tint */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to right, rgba(100, 20, 35, 0.35) 0%, rgba(80, 15, 30, 0.25) 30%, transparent 60%)'
          }}
        />
      </div>

      {/* Full width bottom fade (DARK THEME ONLY) */}
      <div 
        className="absolute left-0 bottom-0 w-full h-[200px] pointer-events-none z-10 dark-only"
        style={{
          background: 'linear-gradient(to bottom, transparent 0%, rgba(10,10,15,0.5) 40%, rgb(10,10,15) 100%)'
        }}
      />

      {/* RIGHT SIDE - Subtle red glow (DARK THEME ONLY) */}
      <div className="absolute right-0 top-0 w-1/2 h-full pointer-events-none dark-only">
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at 80% 50%, rgba(220, 38, 78, 0.12) 0%, transparent 50%)'
          }}
        />
      </div>

      {/* Light theme decorative elements */}
      <div className="light-decor-blob-1" />
      <div className="light-decor-blob-2" />
      <div className="light-decor-circle-1" style={{ top: '12%', left: '5%' }} />
      <div className="light-decor-circle-2" style={{ bottom: '25%', right: '8%', width: '100px', height: '100px' }} />
      <div className="light-decor-dot-1" style={{ top: '18%', right: '15%' }} />
      <div className="light-decor-dot-2" style={{ bottom: '40%', left: '12%' }} />
      <div className="light-decor-line-1" style={{ top: '8%', left: '2%' }} />
      <div className="light-decor-line-2" style={{ bottom: '20%', right: '3%' }} />

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:80px_80px] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-20 lg:pt-0">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-screen py-20 lg:py-24">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="order-2 lg:order-1 text-center lg:text-left"
          >
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.6 }}
              className="text-sm font-medium text-primary uppercase tracking-widest mb-4"
            >
              I am
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="text-[1.75rem] sm:text-4xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-4"
            >
              <span className="text-foreground">MD Mustakim </span>
              <span className="text-gradient">Ahmed</span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="mb-6 h-16"
            >
              {/* Wipe In/Out Animation */}
              <div className="relative inline-flex items-center">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-foreground">
                  {visibleText}
                </h2>
                {/* Cursor Line */}
                <motion.span 
                  className="inline-block w-[3px] h-7 lg:h-8 bg-primary ml-0.5"
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity, ease: "easeInOut" }}
                />
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-base sm:text-lg text-muted-foreground/90 mb-6 max-w-xl mx-auto lg:mx-0 leading-relaxed font-medium"
            >
              Product-focused Full-Stack Product Developer with hands-on experience building and deploying 
              real-world software systems. My experience includes marketing, designing, and managing complete digital exam, 
              booking, eligibility checker, eCommerce, website, and database management systems for a language learning 
              institute I co-founded, through which <span className="text-primary font-medium">nearly 4,000 students</span> have 
              completed courses over the past two years.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.6 }}
              className="mb-8 max-w-xl mx-auto lg:mx-0"
            >
              <div className="glass-strong rounded-xl p-4 border-l-4 border-primary">
                <p className="text-[17px] text-foreground font-bold">
                  "Clients are welcome to share any specific requirement or technical challenge to evaluate my capabilities."
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start flex-wrap"
            >
              <Button 
                variant="gradient" 
                size="lg"
                onClick={() => setShowProjectModal(true)}
                className="group"
              >
                View Projects
                <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => scrollToSection('contact')}
                className="hover:border-primary/50 hover:glow"
              >
                <Mail className="w-4 h-4" />
                Get in Touch
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                asChild
                className="hover:border-primary/50 hover:glow"
              >
                <a href="/cv.pdf" download="Mustakim_Ahmed_CV.pdf">
                  <Download className="w-4 h-4" />
                  Download CV
                </a>
              </Button>
              <Button 
                variant="gradient"
                size="lg"
                asChild
                data-testid="book-call-btn"
                className="group"
              >
                <Link to="/book-call">
                  <Calendar className="w-4 h-4" />
                  Book a Call
                </Link>
              </Button>
            </motion.div>

            {/* Social links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="flex flex-col items-center lg:items-start gap-5 mt-8"
            >
              <span className="text-lg font-bold text-foreground">Find me on</span>
              <div className="flex items-center gap-4">
                <motion.a
                  href="https://www.linkedin.com/in/ahm-mustakim/"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-14 h-14 rounded-full bg-[#2a2a2a] flex items-center justify-center text-white hover:bg-primary transition-all duration-300"
                >
                  <Linkedin className="w-6 h-6" />
                </motion.a>
                <motion.a
                  href="https://www.facebook.com/ahm.mustakim"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-14 h-14 rounded-full bg-[#2a2a2a] flex items-center justify-center text-white hover:bg-primary transition-all duration-300"
                >
                  <Facebook className="w-6 h-6" />
                </motion.a>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Image - Cutout style with gradient background */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            className="order-1 lg:order-2 relative"
          >
            <div className="relative max-w-md mx-auto lg:max-w-lg">
              {/* Background shape with gradient */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[90%] h-[75%] bg-gradient-to-t from-primary/20 via-primary/5 to-transparent rounded-t-full"
              />
              
              {/* Decorative circle */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="absolute top-8 right-0 w-24 h-24 lg:w-32 lg:h-32 border-2 border-primary/30 rounded-full"
              />
              
              {/* Main image container */}
              <div className="relative z-10">
                <img
                  src={heroImage}
                  alt="MD Mustakim Ahmed - Full-Stack Product Developer"
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  className="w-full h-auto object-contain drop-shadow-2xl max-h-[600px]"
                />
              </div>

              {/* Floating stats badge - top right */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9, duration: 0.5 }}
                className="absolute top-8 -right-2 lg:top-12 lg:right-4 glass-strong rounded-2xl p-4 shadow-xl"
              >
                <p className="text-2xl font-bold text-gradient">4,000+</p>
                <p className="text-sm text-muted-foreground">Students Served</p>
              </motion.div>

              {/* Award badge - changed to Experience */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="absolute top-1/3 -left-2 lg:-left-4 glass-strong rounded-2xl p-3 shadow-xl"
              >
                <p className="text-sm font-semibold text-primary">8+ Years</p>
                <p className="text-sm text-muted-foreground">Experience in Web Dev</p>
                <p className="text-sm text-muted-foreground/70">& Digital Branding</p>
              </motion.div>

              {/* Experience badge - bottom center */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.5 }}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 glass-strong rounded-full px-5 py-2 shadow-xl"
              >
                <p className="text-sm font-semibold text-foreground whitespace-nowrap">8+ Years Experience</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:block"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex items-start justify-center p-2"
        >
          <div className="w-1 h-2 bg-primary/50 rounded-full" />
        </motion.div>
      </motion.div>

      {/* Projects Modal */}
      <AnimatePresence>
        {showProjectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
            onClick={() => setShowProjectModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="relative max-w-lg w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Outer container for border effect */}
              <div className="relative rounded-2xl p-[3px]" style={{ overflow: 'hidden' }}>
                {/* Rotating Conic Gradient Border */}
                <motion.div 
                  className="absolute"
                  style={{
                    width: '200%',
                    height: '200%',
                    left: '-50%',
                    top: '-50%',
                    background: 'conic-gradient(from 0deg at 50% 50%, hsl(350, 100%, 60%) 0deg, hsl(340, 90%, 55%) 30deg, hsl(320, 85%, 50%) 60deg, hsl(300, 70%, 45%) 90deg, transparent 150deg, transparent 210deg, hsl(20, 90%, 55%) 270deg, hsl(10, 95%, 58%) 300deg, hsl(350, 100%, 60%) 360deg)',
                  }}
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
                
                {/* Glass Inner Content */}
                <div className="relative z-10 modal-content backdrop-blur-xl rounded-[13px] overflow-hidden">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between p-6 border-b border-white/10 modal-header">
                    <div>
                      <h2 className="text-xl font-bold text-foreground">Project Access</h2>
                      <p className="text-sm text-muted-foreground mt-1">Live demonstration available</p>
                    </div>
                    <button
                      onClick={() => setShowProjectModal(false)}
                      className="w-10 h-10 rounded-full border border-white/20 modal-close-btn flex items-center justify-center hover:bg-white/10 transition-colors"
                    >
                      <X className="w-5 h-5 text-muted-foreground" />
                    </button>
                  </div>

                  {/* Modal Content */}
                  <div className="p-6">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
                        <Calendar className="w-8 h-8 text-primary" />
                      </div>
                      <p className="text-foreground text-lg leading-relaxed mb-2">
                        Most of my projects require secure login access to demonstrate full functionality.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        Please contact me to schedule a call, and I'll walk you through the systems live.
                      </p>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="p-6 border-t border-white/10 bg-white/5 modal-footer">
                    <div className="flex gap-3 justify-center">
                      <Button
                        onClick={() => {
                          setShowProjectModal(false);
                          scrollToSection('contact');
                        }}
                        className="px-6 bg-gradient-primary hover:opacity-90"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Contact Me
                      </Button>
                      <Button
                        onClick={() => setShowProjectModal(false)}
                        variant="outline"
                        className="px-6"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export { HeroSection };
